<script lang="ts">
  // Proposals list — used by the Pending Review tab (status=open),
  // the Changes Requested tab, and the Recently Completed tab
  // (status=completed). Each card summarises a proposal and links to
  // either the Review interface or the merged proposal's audit entry.
  //
  // Per INTERFACE-SPECIFICATION.md section 8.1, a card whose status is
  // 'changes_requested' renders the most recent reviewer comment
  // inline (author, timestamp, body) and exposes [View full detail],
  // [Revise], and [Discard proposal] actions. The proposer sees the
  // outstanding feedback without opening the proposal.

  import { onMount } from 'svelte';
  import { api, ApiError } from '../lib/api';
  import { findProduct } from '../lib/products';
  import { formatLocalDate, relativeTimeAgo, formatCommentTimestamp, formatEffectiveTimestamp, formatClampedFromNote } from '../lib/formatters';
  import type { ProposalSummary } from '../lib/types';

  interface Props {
    // 'open' returns all currently-open proposals; each card's status
    // reflects review state (open vs changes_requested) via a badge.
    // 'changes_requested' filters to just those with an outstanding
    // REQUEST_CHANGES review.
    // 'completed' returns the Recently completed bucket (merged plus
    // closed-without-merge), each card badged by its own status.
    status: 'open' | 'merged' | 'changes_requested' | 'closed' | 'completed';
    onPick: (prNumber: number) => void;
    // Required when status can include 'changes_requested' cards. The
    // [Revise] button on a changes-requested card calls this — the
    // parent (Changes.svelte) navigates to /changes/{tab}/{n}/revise.
    onRevise?: (prNumber: number) => void;
  }

  const { status, onPick, onRevise }: Props = $props();

  let proposals = $state<ProposalSummary[]>([]);
  let loading = $state<boolean>(true);
  let error = $state<string | null>(null);
  let discardError = $state<string | null>(null);
  let discardingPr = $state<number | null>(null);

  onMount(() => {
    void load();
  });

  async function load() {
    loading = true;
    error = null;
    try {
      proposals = await api.listProposals(status);
    } catch (err) {
      error = err instanceof ApiError ? `${err.code}: ${err.message}` : err instanceof Error ? err.message : 'list_proposals_failed';
    } finally {
      loading = false;
    }
  }

  function productLabel(p: ProposalSummary): string {
    const desc = findProduct(p.jurisdiction, p.product);
    return desc ? `${desc.label} · ${desc.customerLabel}` : `${p.jurisdiction}/${p.product}`;
  }

  // Empty-state copy. The bare status token surfaces as "No
  // changes_requested proposals" — underscore-and-all — which reads
  // like internal API output. Map each bucket to a human sentence.
  function emptyMessage(s: Props['status']): string {
    switch (s) {
      case 'open': return 'No proposals awaiting review.';
      case 'changes_requested': return 'No proposals awaiting revision.';
      case 'merged': return 'No merged proposals yet.';
      case 'closed': return 'No closed proposals.';
      case 'completed': return 'Nothing in Recently completed yet.';
      default: return 'No proposals.';
    }
  }

  // Recently completed grows unbounded as merges accumulate. The
  // server caps the bucket to the most recent 20 entries so the
  // proxy Worker stays inside its wall-time budget; the SPA footer
  // tells the operator the cap is in effect so missing older entries
  // are not mistaken for data loss.
  const RECENTLY_COMPLETED_CAP = 20;
  const showsCapNote = $derived(
    status === 'completed' && proposals.length >= RECENTLY_COMPLETED_CAP
  );

  function reviewVerdictLabel(state: string | undefined): string {
    if (state === 'CHANGES_REQUESTED') return 'Changes requested';
    if (state === 'APPROVED') return 'Approved';
    return state ?? '';
  }

  async function handleDiscard(p: ProposalSummary) {
    if (typeof window === 'undefined') return;
    const confirmed = window.confirm(
      `Discard proposal #${p.pr_number}?\n\n` +
      'The PR will close without merging and will appear in Recently ' +
      'Completed with a Closed badge. The change will not become live. ' +
      'This action cannot be undone — to propose the change again, start ' +
      'a fresh draft.'
    );
    if (!confirmed) return;
    const reason = window.prompt(
      'Reason for discarding (optional, recorded on the PR thread for audit):',
      ''
    );
    discardError = null;
    discardingPr = p.pr_number;
    try {
      await api.discardProposal(p.pr_number, reason || undefined);
      await load();
    } catch (err) {
      discardError = err instanceof ApiError
        ? `${err.code}: ${err.message}`
        : err instanceof Error ? err.message : 'discard_failed';
    } finally {
      discardingPr = null;
    }
  }
</script>

<section>
  {#if loading}
    <p class="muted">Loading proposals…</p>
  {:else if error}
    <div class="banner banner-fail">{error}</div>
  {:else if proposals.length === 0}
    <p class="muted">{emptyMessage(status)}</p>
  {:else}
    {#if discardError}
      <div class="banner banner-fail">{discardError}</div>
    {/if}
    <ul class="proposal-list">
      {#each proposals as p (p.pr_number)}
        <li
          class="proposal-card"
          class:is-changes-requested={p.status === 'changes_requested'}
          class:is-merged={p.status === 'merged'}
          class:is-closed={p.status === 'closed'}
        >
          <header>
            <strong>#{p.pr_number} · {productLabel(p)}</strong>
            <span class="muted">value type: {p.value_type}</span>
            <span class="muted">{p.cell_change_count} cell change{p.cell_change_count === 1 ? '' : 's'}</span>
            {#if p.status === 'changes_requested'}
              <span class="status-badge status-badge--changes-requested">Changes requested</span>
            {:else if p.status === 'merged'}
              <span class="status-badge status-badge--merged">Merged</span>
            {:else if p.status === 'closed'}
              <span class="status-badge status-badge--closed">Closed (not merged)</span>
            {/if}
          </header>
          <p class="muted">{p.change_description}</p>
          <dl class="proposal-card__meta">
            <div><dt>Proposed by</dt><dd>{p.proposed_by ?? '—'}</dd></div>
            <div><dt>Reviewer</dt><dd>{p.reviewer ?? '—'}</dd></div>
            <div>
              <dt>Effective from</dt>
              <dd>
                {formatEffectiveTimestamp(p.effective_from)}
                {#if p.proposed_effective_from}
                  <div class="clamp-note muted">{formatClampedFromNote(p.proposed_effective_from)}</div>
                {/if}
              </dd>
            </div>
            <div><dt>Updated</dt><dd>{relativeTimeAgo(p.updated_at)}</dd></div>
          </dl>

          {#if p.status === 'changes_requested' && p.latest_active_review}
            <!-- INTERFACE-SPECIFICATION.md section 8.1: the most recent
                 reviewer comment renders inline so the proposer sees
                 the outstanding feedback without opening the proposal.
                 The full chronological history is on the Revise
                 interface (section 8.4) via "View earlier comments". -->
            <aside class="reviewer-comment">
              <header>
                <strong>{p.latest_active_review.author ?? 'reviewer'}</strong>
                <span class="muted">·</span>
                <span class="muted">{formatCommentTimestamp(p.latest_active_review.submitted_at)}</span>
                <span class="muted">·</span>
                <span class="reviewer-comment__verdict">{reviewVerdictLabel(p.latest_active_review.state)}</span>
              </header>
              <p class="reviewer-comment__body">{p.latest_active_review.body}</p>
            </aside>
          {/if}

          <div class="proposal-card__actions">
            {#if p.status === 'changes_requested'}
              <button class="btn" type="button" onclick={() => onPick(p.pr_number)}>View full detail</button>
              {#if onRevise}
                <button class="btn btn-primary" type="button" onclick={() => onRevise(p.pr_number)}>Revise →</button>
              {/if}
              <button
                class="btn"
                type="button"
                onclick={() => handleDiscard(p)}
                disabled={discardingPr === p.pr_number}
              >
                {discardingPr === p.pr_number ? 'Discarding…' : 'Discard proposal'}
              </button>
            {:else}
              <button class="btn" type="button" onclick={() => onPick(p.pr_number)}>
                {status === 'open' ? 'Review →' : 'View details →'}
              </button>
            {/if}
            {#if status === 'merged'}
              <a class="btn-link" href="/governance/audit?hash={p.pr_number}">Audit by hash →</a>
            {/if}
          </div>
        </li>
      {/each}
    </ul>
    {#if showsCapNote}
      <p class="muted proposals-list__cap-note">
        Showing the {RECENTLY_COMPLETED_CAP} most recent. Older completed proposals are preserved in the audit trail but not displayed here.
      </p>
    {/if}
  {/if}
</section>

<style>
  .proposal-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: var(--space-3); }
  .proposal-card {
    background: var(--surface-card);
    border: 1px solid var(--surface-line-soft);
    border-radius: var(--radius-md);
    padding: var(--space-4);
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .proposal-card header {
    display: flex;
    gap: var(--space-3);
    align-items: baseline;
    flex-wrap: wrap;
  }
  .proposal-card__meta {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: var(--space-3);
    margin: 0;
  }
  .proposal-card__meta dt { font-size: var(--type-sm); color: var(--surface-muted); }
  .proposal-card__meta dd { margin: 0; font-size: var(--type-sm); }
  .proposal-card__actions { display: flex; gap: var(--space-3); margin-top: var(--space-2); flex-wrap: wrap; }
  /* Changes-requested cards stand out so the proposer can see at a
   * glance that there is outstanding feedback to address. The accent
   * matches the "warn" hue rather than "fail" — REQUEST_CHANGES is a
   * conversation marker, not a permanent block. */
  .proposal-card.is-changes-requested {
    border-color: var(--accent-warn, var(--surface-line));
    box-shadow: inset 4px 0 0 0 var(--accent-warn, var(--surface-line));
  }
  .status-badge {
    font-size: var(--type-xs);
    padding: 0.1rem 0.5rem;
    border-radius: var(--radius-sm);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .status-badge--changes-requested {
    background: var(--accent-warn-bg, var(--surface-card));
    color: var(--accent-warn, var(--surface-muted));
    border: 1px solid var(--accent-warn, var(--surface-line));
  }
  /* Inline reviewer comment block (INTERFACE-SPECIFICATION.md §8.1). */
  .reviewer-comment {
    background: var(--accent-warn-bg, var(--surface-card));
    border: 1px solid var(--accent-warn, var(--surface-line));
    border-radius: var(--radius-sm);
    padding: var(--space-2) var(--space-3);
    margin-top: var(--space-2);
  }
  .reviewer-comment header {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
    align-items: baseline;
    font-size: var(--type-sm);
  }
  .reviewer-comment__verdict {
    font-weight: 600;
    text-transform: uppercase;
    font-size: var(--type-xs);
    letter-spacing: 0.04em;
    color: var(--accent-warn);
  }
  .reviewer-comment__body {
    margin: var(--space-1) 0 0 0;
    font-size: var(--type-sm);
    white-space: pre-wrap;
  }
  /* Merged — the proposal landed and the change is live (or queued for
   * its effective-from instant). */
  .proposal-card.is-merged {
    border-color: var(--accent-pass, var(--surface-line));
    box-shadow: inset 4px 0 0 0 var(--accent-pass, var(--surface-line));
  }
  .status-badge--merged {
    background: var(--accent-pass-bg, var(--surface-card));
    color: var(--accent-pass, var(--surface-muted));
    border: 1px solid var(--accent-pass, var(--surface-line));
  }
  .proposal-card.is-closed {
    opacity: 0.75;
    border-color: var(--surface-line);
    box-shadow: inset 4px 0 0 0 var(--surface-muted, var(--surface-line));
  }
  .status-badge--closed {
    background: var(--surface-card);
    color: var(--surface-muted);
    border: 1px solid var(--surface-line);
  }
  .proposals-list__cap-note {
    margin-top: var(--space-3);
    padding-top: var(--space-2);
    border-top: 1px solid var(--surface-line-soft);
    font-size: var(--type-sm);
    font-style: italic;
  }
</style>
